import React, { useEffect, useState } from 'react';
import TextWinds from '../../../common/TextWinds';
import {
  ActivityType,
  StudentRecentActivityItem,
  TeacherRecentActivityItem,
} from '../../../../constants/types/lms/dashboard/type';
import { LmsInfoTypes } from '../../../../constants/types';
import { useAppDispatch } from '../../../../store/hook';
import { openAlert } from '../../../../store/features/modalSlice';
import {
  formatToKstDate,
  getErrorMessages,
} from '../../../../api/utils/util-func';
import { getTeacherStudentRecentActivityList } from '../../../../api/utils/lms/dashboard/api';
import { LoadingSpinner } from '../../../Loading/LoadingSpinner';

interface RecentActivityProps {
  classId: string;
  lmsLists: LmsInfoTypes;
}

type ActivityColor = 'blue' | 'green' | 'red' | 'gray';

const activityColorMap: Record<ActivityColor, string> = {
  blue: 'bg-blue-500',
  green: 'bg-green-500',
  red: 'bg-red-500',
  gray: 'bg-gray-500',
};

/**
 * 개편 LMS (교사) - 클램스 홈 페이지 > 최근 활동
 */
const RecentActivity = ({ classId, lmsLists }: RecentActivityProps) => {
  const dispatch = useAppDispatch();

  const [teacherRecentActivityList, setTeacherRecentActivityList] = useState<
    TeacherRecentActivityItem[]
  >([]);
  const [studentRecentActivityList, setStudentRecentActivityList] = useState<
    StudentRecentActivityItem[]
  >([]);
  const [isLoadingRecentActivity, setIsLoadingRecentActivity] =
    useState<boolean>(false);

  useEffect(() => {
    if (!lmsLists.course || !classId) return;

    const fetchRecentActivityList = async () => {
      try {
        setIsLoadingRecentActivity(true);

        const response = await getTeacherStudentRecentActivityList({
          classId,
        });

        setTeacherRecentActivityList(response.teacherActivities ?? []);
        setStudentRecentActivityList(response.studentActivities ?? []);
      } catch (error) {
        dispatch(openAlert({ message: getErrorMessages(error) }));
      } finally {
        setIsLoadingRecentActivity(false);
      }
    };

    fetchRecentActivityList();
  }, [lmsLists.course, classId, dispatch]);

  if (isLoadingRecentActivity) {
    return <LoadingSpinner message="데이터 조회중" />;
  }

  return (
    <div className="grid gap-5 xl:grid-cols-2">
      <RecentActivitySection
        title="학생 최근 활동"
        items={studentRecentActivityList}
        getMessage={(item) => item.message}
        getSubText={(item) => item.studentName}
        getDate={(item) => item.completedAt}
        getColor={() => activityColorMap.green}
      />

      <RecentActivitySection
        title="교사 최근 활동"
        items={teacherRecentActivityList}
        getMessage={(item) => item.message}
        getSubText={(item) => item.students}
        getDate={(item) => item.createdAt}
        getColor={(item) =>
          activityColorMap[
            item.type === ActivityType.ASSIGNMENT_COMPLETED ? 'green' : 'blue'
          ]
        }
      />
    </div>
  );
};

export default RecentActivity;

interface RecentActivitySectionProps<T extends { id: number | string }> {
  title: string;
  items: T[];
  getMessage: (item: T) => string;
  getSubText: (item: T) => string;
  getDate: (item: T) => string;
  getColor: (item: T) => string;
}

const RecentActivitySection = <T extends { id: number | string }>({
  title,
  items,
  getMessage,
  getSubText,
  getDate,
  getColor,
}: RecentActivitySectionProps<T>) => {
  if (items.length === 0) return null;

  return (
    <section className="rounded-xl bg-white p-4 shadow-card xl:p-6">
      <div className="mb-2 flex items-start justify-between gap-4">
        <TextWinds type="title_h3">{title}</TextWinds>
      </div>

      <div className="flex flex-col">
        {items.map((item) => {
          const dateText = formatToKstDate(getDate(item), 'YYYY.MM.DD HH:mm');

          return (
            <div
              key={item.id}
              className="flex items-start justify-between gap-4 border-b border-gray2 py-2 last:border-b-0"
            >
              <div className="flex min-w-0 items-start gap-3">
                <div
                  className={[
                    'mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full',
                    getColor(item),
                  ].join(' ')}
                />

                <div className="min-w-0 flex-1">
                  <TextWinds
                    type="content_body2"
                    className="whitespace-normal break-keep text-sm leading-snug xl:text-base"
                  >
                    {getMessage(item)}
                  </TextWinds>

                  <TextWinds
                    type="caption1"
                    color="gray8"
                    className="mt-0.5 whitespace-normal break-keep"
                  >
                    {getSubText(item)}
                  </TextWinds>

                  <TextWinds
                    type="caption1"
                    color="gray8"
                    className="mt-1 block xl:hidden"
                  >
                    {dateText}
                  </TextWinds>
                </div>
              </div>

              <TextWinds
                type="caption1"
                color="gray8"
                className="hidden shrink-0 pt-1 xl:block"
              >
                {dateText}
              </TextWinds>
            </div>
          );
        })}
      </div>
    </section>
  );
};
